class TptVideoBanner {
    constructor() {
      this.init();
      this.pauseIconContent = '<rect x="6" y="4" width="4" height="16" fill="currentColor"/><rect x="14" y="4" width="4" height="16" fill="currentColor"/>';
      this.playIconContent = '<path d="M8 5.14v13.72L19 12 8 5.14z" fill="currentColor"/>';
    }
  
    init() {
      // Wait for DOM to be fully loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupVideoBanners());
      } else {
        this.setupVideoBanners();
      }
    }
  
    setupVideoBanners() {
      const videoBanners = document.querySelectorAll('.tpt-video-banner-1');
      
      videoBanners.forEach(banner => {
        this.setupVideoBanner(banner);
      });
    }
  
    setupVideoBanner(banner) {
      const video = banner.querySelector('.tpt-video-banner-1_video');
      const pauseButton = banner.querySelector('.tpt-video-banner-1_pause-button');
      const iframe = banner.querySelector('.tpt-video-banner-1_iframe');
      const embedContainer = banner.querySelector('.tpt-video-4_embed');
      const imageContainer = banner.querySelector('.tpt-video-banner-1_image');
      const container = banner.querySelector('.tpt-video-banner-1_container');
      
      // If only image is present, hide the pause button
      if (imageContainer && !video && !iframe && !embedContainer) {
        if (pauseButton) {
          pauseButton.style.display = 'none';
        }
        // Mark container as loaded for image
        if (container) {
          container.classList.add('loaded');
        }
        return;
      }
      
      // Setup video loading state
      if (video) {
        this.setupVideoElement(video, pauseButton, container);
      }
  
      // Setup iframe handling (limited functionality due to cross-origin restrictions)
      if (iframe || embedContainer) {
        this.setupIframeElement(iframe || embedContainer, pauseButton);
      }
  
      // Add click event to pause button if it exists
      if (pauseButton) {
        pauseButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.togglePlayPause(banner);
        });
  
        // Keyboard accessibility
        pauseButton.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.togglePlayPause(banner);
          }
        });
      }
    }
  
    setupVideoElement(video, pauseButton, container) {
      // Handle video loading states
      video.addEventListener('loadeddata', () => {
        video.setAttribute('data-loaded', 'true');
        if (container) {
          container.classList.add('loaded');
        }
      });
  
      video.addEventListener('canplay', () => {
        // If autoplay is disabled and we have a pause button, ensure video is paused initially
        if (pauseButton && !video.autoplay) {
          video.pause();
          this.updateButtonState(pauseButton, 'paused');
        }
      });
  
      video.addEventListener('error', () => {
        console.warn('Video failed to load');
        if (pauseButton) {
          pauseButton.style.display = 'none';
        }
      });
  
      // Update button state based on video state (only if pause button exists)
      if (pauseButton) {
        video.addEventListener('play', () => {
          this.updateButtonState(pauseButton, 'playing');
        });
  
        video.addEventListener('pause', () => {
          this.updateButtonState(pauseButton, 'paused');
        });
  
        // Handle video ended
        video.addEventListener('ended', () => {
          this.updateButtonState(pauseButton, 'paused');
        });
      }
  
      // Try to play video if autoplay is enabled but blocked
      if (video.autoplay) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Autoplay was prevented:', error);
            // If autoplay fails and we don't have a pause button, create one
            if (!pauseButton) {
              this.createPauseButton(video.closest('.tpt-video-banner-1'));
            }
          });
        }
      }
    }
  
    setupIframeElement(iframe, pauseButton) {
      // For iframes and embeds, we have limited control
      // YouTube and Vimeo APIs would be needed for full control
      
      const loadHandler = () => {
        // Iframe/embed loaded successfully
        if (pauseButton) {
          pauseButton.setAttribute('data-iframe-loaded', 'true');
        }
      };

      const errorHandler = () => {
        console.warn('Iframe/embed video failed to load');
        if (pauseButton) {
          pauseButton.style.display = 'none';
        }
      };

      if (iframe.tagName === 'IFRAME') {
        iframe.addEventListener('load', loadHandler);
        iframe.addEventListener('error', errorHandler);
      } else {
        // For embed containers, check if they contain iframes
        const iframeInEmbed = iframe.querySelector('iframe');
        if (iframeInEmbed) {
          iframeInEmbed.addEventListener('load', loadHandler);
          iframeInEmbed.addEventListener('error', errorHandler);
        } else {
          // For other embed types, assume they loaded successfully
          setTimeout(loadHandler, 100);
        }
      }
    }
  
    togglePlayPause(banner) {
      const video = banner.querySelector('.tpt-video-banner-1_video');
      const pauseButton = banner.querySelector('.tpt-video-banner-1_pause-button');
      const iframe = banner.querySelector('.tpt-video-banner-1_iframe');
      const embedContainer = banner.querySelector('.tpt-video-4_embed');
  
      if (video) {
        // Handle HTML5 video
        if (video.paused) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(e => {
              console.warn('Failed to play video:', e);
            });
          }
        } else {
          video.pause();
        }
      } else if (iframe || embedContainer) {
        // Handle iframe videos or embeds (limited functionality)
        this.handleIframeToggle(iframe || embedContainer, pauseButton);
      }
    }
  
    handleIframeToggle(iframe, pauseButton) {
      // This is a simplified approach for iframe videos and embeds
      // In a production environment, you would integrate with:
      // - YouTube IFrame API for YouTube videos
      // - Vimeo Player API for Vimeo videos
      
      const currentState = pauseButton.getAttribute('data-state') || 'paused';
      
      if (currentState === 'paused') {
        this.updateButtonState(pauseButton, 'playing');
        console.log('Iframe/embed video play requested (requires API integration)');
      } else {
        this.updateButtonState(pauseButton, 'paused');
        console.log('Iframe/embed video pause requested (requires API integration)');
      }
    }
  
    updateButtonState(button, state) {
      const controlIcon = button.querySelector('.tpt-video-banner-1_control-icon');
      
      if (!controlIcon) return;
  
      button.setAttribute('data-state', state);
  
      if (state === 'playing') {
        // Show pause icon (two rectangles)
        controlIcon.innerHTML = this.pauseIconContent;
        button.setAttribute('aria-label', 'Pause video');
      } else {
        // Show play icon (triangle)
        controlIcon.innerHTML = this.playIconContent;
        button.setAttribute('aria-label', 'Play video');
      }
    }
  
    // Create pause button dynamically if autoplay fails
    createPauseButton(banner) {
      const container = banner.querySelector('.tpt-video-banner-1_container');
      if (!container || container.querySelector('.tpt-video-banner-1_pause-button')) {
        return; // Button already exists or no container
      }
  
      const pauseButton = document.createElement('button');
      pauseButton.className = 'tpt-video-banner-1_pause-button';
      pauseButton.setAttribute('data-pause-button', '');
      pauseButton.setAttribute('aria-label', 'Play video');
      
      pauseButton.innerHTML = `
        <svg class="tpt-video-banner-1_control-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
          ${this.playIconContent}
        </svg>
      `;
      
      container.appendChild(pauseButton);
      
      // Add event listeners to the new button
      pauseButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.togglePlayPause(banner);
      });
  
      pauseButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.togglePlayPause(banner);
        }
      });
      
      this.updateButtonState(pauseButton, 'paused');
    }
  
    // Utility method to handle responsive behavior
    handleResize() {
      const videoBanners = document.querySelectorAll('.tpt-video-banner-1');
      
      videoBanners.forEach(banner => {
        const video = banner.querySelector('.tpt-video-banner-1_video');
        if (video) {
          // Force video to maintain aspect ratio on resize
          this.maintainVideoAspectRatio(video);
        }
      });
    }
  
    maintainVideoAspectRatio(video) {
      // Ensure video maintains proper aspect ratio on window resize
      const container = video.closest('.tpt-video-banner-1_container');
      if (!container) return;
  
      const containerRect = container.getBoundingClientRect();
      
      // Only adjust if video has loaded and we have dimensions
      if (video.videoWidth && video.videoHeight) {
        const videoAspectRatio = video.videoWidth / video.videoHeight;
        const containerAspectRatio = containerRect.width / containerRect.height;
  
        if (containerAspectRatio > videoAspectRatio) {
          // Container is wider than video aspect ratio
          video.style.width = '100%';
          video.style.height = 'auto';
        } else {
          // Container is taller than video aspect ratio
          video.style.width = 'auto';
          video.style.height = '100%';
        }
      }
    }
  }
  
  // Initialize the video banner functionality
  const tptVideoBanner = new TptVideoBanner();
  
  // Handle window resize for responsive video behavior
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      tptVideoBanner.handleResize();
    }, 250);
  });
  
  // Handle visibility change to pause videos when tab is not visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Pause all videos when tab becomes hidden
      const videos = document.querySelectorAll('.tpt-video-banner-1_video');
      videos.forEach(video => {
        if (!video.paused) {
          video.pause();
          video.setAttribute('data-was-playing', 'true');
        }
      });
    } else {
      // Resume videos that were playing when tab becomes visible again
      const videos = document.querySelectorAll('.tpt-video-banner-1_video[data-was-playing="true"]');
      videos.forEach(video => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            console.warn('Failed to resume video:', e);
          });
        }
        video.removeAttribute('data-was-playing');
      });
    }
  });
  
  // Intersection Observer for performance optimization
  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('.tpt-video-banner-1_video');
        if (!video) return;
  
        if (entry.isIntersecting) {
          // Video is visible, ensure it can play if it should
          if (video.paused && video.getAttribute('data-should-play') === 'true') {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(e => {
                console.warn('Failed to play video on intersection:', e);
              });
            }
          }
        } else {
          // Video is not visible, pause to save resources (only if autoplay is enabled)
          if (!video.paused && video.autoplay) {
            video.setAttribute('data-should-play', 'true');
            video.pause();
          }
        }
      });
    }, {
      threshold: 0.25 // Trigger when 25% of the video is visible
    });
  
    // Observe all video banners
    document.addEventListener('DOMContentLoaded', () => {
      const videoBanners = document.querySelectorAll('.tpt-video-banner-1');
      videoBanners.forEach(banner => {
        videoObserver.observe(banner);
      });
    });
  }
  
  // Handle user interaction requirement for autoplay
  document.addEventListener('click', () => {
    // After first user interaction, try to play any videos that failed autoplay
    const videos = document.querySelectorAll('.tpt-video-banner-1_video[autoplay]:not([data-play-attempted])');
    videos.forEach(video => {
      video.setAttribute('data-play-attempted', 'true');
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            console.warn('Failed to play video after user interaction:', e);
          });
        }
      }
    });
  }, { once: false });